import {Component, effect, input, InputSignal, output, signal, WritableSignal} from '@angular/core';
import {EvenementService} from '../../../shared/services/evenement.service';
import {finalize} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {DeclarerEvenementDto} from '../../model/declarer-evenement.dto';
import {AlertService, CodeDesignation, DateService} from 'sigale-angular';
import {Duree} from '../../../shared/models/duree.model';
import {DateDemandee} from '../../model/date-demandee.model';
import {DateUtilsService} from '../../../shared/services/date-utils.service';
import {TypeEvenement} from '../../../shared/models/type-evenement.model';
import {PlageSelectionnee} from '../../../shared/models/plage-selectionnee.model';
import {NouvelEvenementDto} from '../../model/nouvel-evenement.dto';

@Component({
	selector: 'app-recapitulatif-declarer-evenement-modal',
	templateUrl: './recapitulatif-declarer-evenement-modal.component.html',
	styleUrl: './recapitulatif-declarer-evenement-modal.component.scss'
})
export class RecapitulatifDeclarerEvenementModal {
	readonly ALERTE_DECLARER_EVENEMENT_MODAL = 'alerteDeclarerEvenementModal';

	visible: InputSignal<boolean> = input.required<boolean>();
	plagesSelectionnees = input.required<PlageSelectionnee[]>();
	type = input.required<CodeDesignation>();
	duree: InputSignal<CodeDesignation> = input.required<CodeDesignation>();
	commentaire: InputSignal<string> = input.required<string>();
	loginUtilisateur: InputSignal<string> = input.required<string>();
	langueUtilisateur: InputSignal<string> = input.required<string>();
	durees = input.required<CodeDesignation[]>();

	visibleChange = output<boolean>();
	notifierCreationEvenementEffective = output<void>();

	datesDemandees = signal<DateDemandee[]>([]);
	enregistrementEnCours: WritableSignal<boolean> = signal(false);
	loading: WritableSignal<boolean> = signal(false);
	estWeekendPresentDansDatesSelectionnees: WritableSignal<boolean> = signal(false);
	// Signal utilisé pour la gestion de la modale par primeng.
	visibleLocal = signal(false);

	constructor(
		private evenementService: EvenementService,
		private alertService: AlertService,
		private dateUtilsService: DateUtilsService,
		private translateService: TranslateService,
		private dateService: DateService) {
		effect(() => {
			this.calculerDateDemandees();
		}, {allowSignalWrites: true});

		// A chaque modification de visible qui est renseigné par le parent (ex à l'ouverture de la modale) on notifie modifie le visible local
		effect(() => {
			this.visibleLocal.set(this.visible());
			if (!this.visible()) {
				this.calculerDateDemandees();
			}
		}, {allowSignalWrites: true});

		// Chaque fois que le visible en local est modifié on emit la valeur au parent.
		effect(() => {
			const val = this.visibleLocal();
			this.visibleChange.emit(val);
		});
	}

	calculerDateDemandees() {
		const plagesSelectionnees = this.plagesSelectionnees();

		this.estWeekendPresentDansDatesSelectionnees.set(false);

		const datesDemandees: DateDemandee[] = [];

		plagesSelectionnees
			.sort((a, b) => a.dateSelectionnee.getTime() - b.dateSelectionnee.getTime())
			.forEach((plage: PlageSelectionnee) => {
				const dateSelectionnee = plage.dateSelectionnee;

				const duree = plage.estDureeModifiable ? this.duree() : this.durees().find(duree => duree.code === plage.duree);

				datesDemandees.push(new DateDemandee(
					dateSelectionnee,
					duree,
					this.commentaire(),
					plage.estDureeModifiable
				));
				// 6 => Samedi, 0 => Dimanche
				if (dateSelectionnee.getDay() === 0 || dateSelectionnee.getDay() === 6) {
					this.estWeekendPresentDansDatesSelectionnees.set(true);
				}
			});
		this.datesDemandees.set(datesDemandees);
	}

	formatterDatePourAffichage(date: Date): string {
		return this.dateUtilsService.formaterDateEnFormatLettre(date, this.langueUtilisateur());
	}

	/**
	 * Réalise la fermeture de la modale suite à annulation ou validation et notifie le parent si les évènements ont été créés.
	 */
	fermerModal(enregistrement = false): void {
		this.visibleChange.emit(false);
		this.visibleLocal.set(false);
		if (enregistrement) {
			// on notifie le parent que les jours de travail ont bien été ajouté.
			this.notifierCreationEvenementEffective.emit();
		}
		this.alertService.clear(this.ALERTE_DECLARER_EVENEMENT_MODAL);
	}

	/**
	 * Ajoute les évènements en base et ferme la modale si tout s'est bien passé.
	 */
	creerEvenements(): void {
		this.enregistrementEnCours.set(true);
		this.alertService.clear(this.ALERTE_DECLARER_EVENEMENT_MODAL);

		const nouveauxEvenements = this.datesDemandees()
			.map(dateDemandee => {
				const duree = dateDemandee.duree.code as Duree;
				const date = this.dateUtilsService.formaterDateEnStringPourEnvoiBack(dateDemandee.date, this.langueUtilisateur());

				return new NouvelEvenementDto(
					date,
					duree,
					dateDemandee.commentaire);
			});

		const type: TypeEvenement = this.type().code as TypeEvenement;

		const declarerEvenements = new DeclarerEvenementDto(
			nouveauxEvenements,
			type,
			this.loginUtilisateur());

		this.evenementService.declarerEvenements(declarerEvenements)
			.pipe(finalize(() => this.enregistrementEnCours.set(false)))
			.subscribe({
				next: () => this.fermerModal(true),
				error: (erreur) => {
					this.alertService.clear(this.ALERTE_DECLARER_EVENEMENT_MODAL);
					this.alertService.error(erreur.error, this.ALERTE_DECLARER_EVENEMENT_MODAL);
				}
			});
	}

	/**
	 * True si le type de l'évènement est absence, False sinon
	 */
	estTypeAbsence(): boolean {
		return this.type().code === TypeEvenement.CONGE || this.type().code === TypeEvenement.TRAVAILLE_AILLEURS;
	}

	/**
	 * Détermine le titre de la modale en fonction de si on pose des jours de télétravail ou des absences.
	 */
	determinerTitreModale(): string {
		return this.estTypeAbsence() ?
			this.translateService.instant('ACCUEIL.DECLARATION_EVENEMENT.POPUP_CONFIRMATION.TITRE_ABSENCE') :
			this.translateService.instant('ACCUEIL.DECLARATION_EVENEMENT.POPUP_CONFIRMATION.TITRE_TELETRAVAIL');
	}

	/**
	 * Détermine l'icône selon si on pose des jours de télétravail ou des absences.
	 */
	determinerIconSelonType(): string {
		if (this.type().code === TypeEvenement.TELETRAVAIL) {
			return 'pi pi-home';
		}

		return this.type().code === TypeEvenement.CONGE ? 'pi pi-sun' : 'pi pi-globe';
	}

	/**
	 * Annule la demande d'un évènement. Et notifie le calendrier pour le supprimer de la sélection
	 * @param dateDemandeeAAnnuler la date demandee à annuler.
	 */
	annulerDemandeEvenement(dateDemandeeAAnnuler: DateDemandee): void {
		this.datesDemandees.update(datesDemandees => datesDemandees.filter(dateDemandee =>
			!this.dateService.sontDatesMemeJour(dateDemandee.date, dateDemandeeAAnnuler.date) || dateDemandee.duree !== dateDemandee.duree));
	}

	/**
	 * Modifie la classe du commentaire si celui-ci dépasse les 255 caractères requis.
	 * @param tailleCommentaire la taille du commentaire.
	 */
	classeAlerteTailleCommentaire(tailleCommentaire: number) {
		if (tailleCommentaire > 255) {
			return 'alerte-commentaire';

		}

		return '';
	}
}
